import { Block, BlockId } from '@epilot/customer-portal-client'
import { isEmpty, isObject } from 'lodash'

import { Attribute, Config } from 'components/shared/attributes/types'

import { getCurrentLanguage, Locale } from './LanguageUtils'

export type BlockMap = Record<BlockId, Block>
export type BlockList = Block[]

export function getLabel(block: Block): string | Record<string, string>
export function getLabel(block: Block, resolveLanguage: true): string
export function getLabel(
  block: Block,
  resolveLanguage?: boolean
): string | Record<string, string> {
  const label = block?.props?.content?.label

  if (resolveLanguage && isObject(label)) {
    const language = getCurrentLanguage()

    return label[language] || label[Locale.EN] || ''
  }

  return label ?? ''
}

const slugify = (input: string): string => {
  return (
    (input || '')
      .toString()
      .normalize('NFC')
      .toLowerCase()
      .trim()
      // Remove explicitly unsafe query param characters (disallow list)
      // whitespace, control chars, DEL, quotes, angle/square/curly brackets,
      // percent, pipe, backslash, caret, at, question, ampersand, equals,
      // semicolon, plus, asterisk, comma, colon, slash
      .replace(/[\p{Cc}\s"'`<>#%{}|\\^[\]@?&=;+*,:/]+/gu, '-')
      .replace(/^-+|-+$/g, '')
  )
}

export const findBlockByType = (
  blocks: BlockMap,
  type: Block['type']
): Block | null => {
  if (isEmpty(blocks)) {
    return null
  }

  return Object.values(blocks).find((block) => block.type === type) ?? null
}

export const findBlocksByType = (
  blocks: BlockMap,
  type: Block['type']
): BlockList => {
  if (isEmpty(blocks)) {
    return []
  }

  return Object.values(blocks).filter((block) => block.type === type)
}

export const findBlockChildren = (
  blocks: BlockMap,
  parentId: Block['parent_id']
): BlockList => {
  if (isEmpty(blocks)) {
    return []
  }

  return Object.values(blocks)
    .filter((block) => block.parentId === parentId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

const GROUP_OR_CUSTOM_BLOCK_MAPPER = {
  group: 'group',
  products: 'products',
  documents: 'documents',
  balance: 'balance',
  workflows: 'workflows'
} as const

const processGroupsAndAttributes = (
  blocks: BlockMap,
  parentId: Block['parent_id']
): Config['groups'] => {
  const sortedChildren = findBlockChildren(blocks, parentId)

  // Process each child and group them by type
  const result = sortedChildren.reduce(
    (acc, block) => {
      if (block.type === 'attribute') {
        if (!acc.currentGroup) {
          acc.currentGroup = {
            id: block.id,
            label: '',
            renderer: block.props?.content?.renderer,
            relation_name: block.props?.content?.relation_name,
            attributes: []
          }
          acc.groups.push(acc.currentGroup)
        }

        // Add attribute to current group
        acc.currentGroup.attributes.push({
          ...(block.props?.content || {}),
          ...(block.props?.design || {}),
          ...(block.props?.visibility || {}),
          name:
            block.props?.content?.relation_attribute_name ||
            block.props?.content?.name
        } as Attribute)
      } else if (GROUP_OR_CUSTOM_BLOCK_MAPPER[block.type]) {
        acc.currentGroup = null

        const group = {
          ...block.props?.content,
          id: block.id,
          label: getLabel(block),
          renderer: block.props?.content?.renderer,
          relation_name: block.props?.content?.relation_name,
          attributes: []
        }

        // Process attributes within this group
        const groupChildren = findBlockChildren(blocks, block.id)

        groupChildren.forEach((child) => {
          if (child.type === 'attribute') {
            group.attributes.push({
              ...(child.props?.content || {}),
              ...(child.props?.design || {}),
              ...(child.props?.visibility || {}),
              name:
                child.props?.content?.relation_attribute_name ||
                child.props?.content?.name
            } as Attribute)
          } else {
            group.attributes.push({
              ...(child.props?.content || {}),
              ...(child.props?.design || {}),
              ...(child.props?.visibility || {}),
              name:
                child.props?.content?.relation_attribute_name ||
                child.props?.content?.name
            } as Attribute)
          }
        })

        acc.groups.push(group)
      } else {
        const group = {
          id: block.id,
          label: getLabel(block),
          renderer: 'custom',
          relation_name: '',
          block: block,
          attributes: []
        }

        acc.groups.push(group)
      }

      return acc
    },
    { groups: [], currentGroup: null }
  )

  return result.groups
}

/**
 * @deprecated use constructTabsConfig instead with block-renderer
 * TODO: remove this after delete all details page
 */
export const buildTabStructure = (blocks: BlockMap): Config[] => {
  if (isEmpty(blocks)) {
    return []
  }

  const tabsBlock = findBlocksByType(blocks, 'tabs')

  if (isEmpty(tabsBlock)) {
    return []
  }

  const tabBlocks = findBlockChildren(blocks, tabsBlock?.[0]?.id)

  return tabBlocks.map((tab) => ({
    id: tab.id,
    label: getLabel(tab),
    name: tab.props?.content?.name || '',
    groups: processGroupsAndAttributes(blocks, tab.id)
  }))
}

export const constructTabsConfig = (blocks: BlockMap, tabsBlock: Block) => {
  if (isEmpty(blocks) || isEmpty(tabsBlock)) {
    return []
  }

  const tabBlocks = findBlockChildren(blocks, tabsBlock.id)

  const slugCounts = new Map<string, number>()

  const makeUniqueSlug = (base: string): string => {
    const current = (slugCounts.get(base) ?? 0) + 1

    slugCounts.set(base, current)

    return current === 1 ? base : `${base}-${current}`
  }

  return tabBlocks.map((tab) => {
    const label = getLabel(tab, true)
    const baseSlug = slugify(label) || 'tab'
    const uniqueSlug = makeUniqueSlug(baseSlug)

    return {
      id: tab.id,
      label,
      name: tab.props?.content?.name ?? '',
      slug: uniqueSlug,
      children: findBlockChildren(blocks, tab.id)
    }
  })
}

export const getOrderedTabsBlocks = (blocks: BlockMap): Block[] => {
  if (isEmpty(blocks)) {
    return []
  }

  return Object.values(blocks)
    .filter((b) => b.type === 'tabs')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

// First tabs block => index 0 (so key becomes 'tab');
// Subsequent tabs blocks => 2, 3, ... (so keys 'tab-2', 'tab-3', ...)
export const getTabParamIndexForBlock = (
  blocks: BlockMap,
  blockId: string
): number => {
  const ordered = getOrderedTabsBlocks(blocks)
  const position = ordered.findIndex((b) => b.id === blockId) + 1 // 1-based

  if (position <= 0) {
    return 0
  }

  return position === 1 ? 0 : position
}

export const getTabParamKeyForIndex = (index: number): string => {
  return index === 0 ? 'tab' : `tab-${index}`
}

export const getTabParamKeyForBlock = (
  blocks: BlockMap,
  blockId: string
): string => {
  return getTabParamKeyForIndex(getTabParamIndexForBlock(blocks, blockId))
}
